import { useMemo, useState } from 'react';
import { Document, pdfjs } from 'react-pdf/dist/cjs';
import 'react-pdf/dist/cjs/Page/AnnotationLayer.css';
import 'react-pdf/dist/cjs/Page/TextLayer.css';
import { useSearchParams } from 'react-router-dom';
import Loader from '../../components/loader';
import Alert from '../../utils/alert';
import { ResourceProps } from '../../utils/reportResourceUtil';
import ResourceRepo from '../../utils/repository/resourceRepo';
import ExtractionIndication from './components/extractionIndication';
import ExtractionModal from './components/extractionModal';
import MultiPageView from '../../components/pdfViewer/multiPageView';
import SinglePageView from '../../components/pdfViewer/singlePageView';
import { PAGE_MODE, SCALE_CONSTANTS } from '../../components/pdfViewer/constants';
import './styles.css';
import {
    CompletedExtractionType,
    ExtractionDataType,
    MetadataType,
    ExtractionResponseType,
} from './types';
import { ExtractionSpecType, PageModeType } from '../../components/pdfViewer/types';
import { validateImageMetadata } from './utils/validations';
import ExtractionHeader from './components/extractionHeader';
import ThumbnailPanel from '../../components/pdfViewer/thumbnailPanel';
import CloneUtils from '../../utils/cloneUtils';
import BrowserStorage from '../../utils/browserStorage';
import AuthService from '../../service/auth';
import SimpleBar from 'simplebar-react';

pdfjs.GlobalWorkerOptions.workerSrc =
    pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDFViewer = () => {
    const userId = AuthService.getUserId();
    const [searchParams] = useSearchParams();
    const [numberOfPages, setNumberOfPages] = useState<number>(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [extractionInProgress, setExtractionInProgress] = useState(false);
    const [scale, setScale] = useState<number>(SCALE_CONSTANTS.DEFAULT_SCALE);
    const [pageMode, setPageMode] = useState<PageModeType>(
        BrowserStorage.getUserPdfViewerPreferences(AuthService.getUserId()).PAGE_MODE
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [extractionSpecs, setExtractionSpecs] = useState<ExtractionSpecType[]>([]);
    const [completedExtractions, setCompletedExtractions] = useState<CompletedExtractionType[]>([]);
    const [screenshotsEnabled, setScreenshotsEnabled] = useState(true);
    const [thumbnailPanelVisible, setThumbnailPanelVisible] = useState<boolean>(
        BrowserStorage.getUserPdfViewerPreferences(AuthService.getUserId()).THUMBNAIL_PANEL_VISIBLE ?? true
    );
    const selectionDisabled = extractionInProgress || !screenshotsEnabled;

    const awsUrl = useMemo(() => {
        return searchParams.get('awsUrl');
    }, [searchParams]);

    const toggleScreenshotsEnabled = () => {
        setScreenshotsEnabled((prev) => !prev);
    };

    const toggleThumbnailPanel = () => {
        setThumbnailPanelVisible((prev) => {
            const next = !prev;
            BrowserStorage.setUserPdfViewerPreferences(userId, {
                ...BrowserStorage.getUserPdfViewerPreferences(userId),
                THUMBNAIL_PANEL_VISIBLE: next,
            });
            return next;
        });
    };

    const thumbnailItems = useMemo(() => {
        if (!numberOfPages) {
            return [];
        }
        return Array.from({ length: numberOfPages }, (_, i) => ({ page: i + 1 }));
    }, [numberOfPages]);

    const handlePageModeChange = (pageMode: PageModeType) => {
        setPageMode(pageMode);
        BrowserStorage.setUserPdfViewerPreferences(userId, {
            ...BrowserStorage.getUserPdfViewerPreferences(userId),
            PAGE_MODE: pageMode,
        });
    };

    const handleSetPage = (pageNumber: number) => {
        let newPage = pageNumber;
        // check to see if the page number is a valid page number
        // prevent user from entering page lower than 1
        if (newPage < 1) {
            newPage = 1;
        }
        //prevent user from entering page greater than the number of pages
        if (newPage > numberOfPages) {
            newPage = numberOfPages;
        }
        //check if the input is a number if not set the page to 1
        if (Number.isNaN(newPage)) {
            newPage = 1;
        }

        setCurrentPage(newPage);
    };
    const handlePrevPage = () => setCurrentPage((prev) => Math.max(1, prev - 1));
    const handleNextPage = () => setCurrentPage((prev) => Math.min(numberOfPages, prev + 1));

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
        setNumberOfPages(numPages);
    };

    const handleCloseExtractionModal = () => {
        setIsModalOpen(false);
    };

    const handleOpenExtractionModal = () => {
        setIsModalOpen(true);
    };

    const clearExtractionSpecs = () => {
        setExtractionSpecs([]);
    };

    const addExtractionSpec = (extractionSpec: ExtractionSpecType) => {
        setExtractionSpecs((prev) => {
            const newExtractionSpecs: ExtractionSpecType[] = CloneUtils.deepClone(prev);
            newExtractionSpecs.push(extractionSpec);
            newExtractionSpecs.sort((a, b) => {
                // first compare by page
                if (a.page !== b.page) {
                    return a.page - b.page;
                }
                // if the extraction specs are on the same page order by y coordinates
                return a.yPercentage - b.yPercentage;
            });
            return newExtractionSpecs;
        });
    };

    const addCompletedExtraction = (completedExtraction: CompletedExtractionType) => {
        setCompletedExtractions((prev) => [...prev, completedExtraction]);
    };

    const handleDeleteExtractionSpecs = () => {
        clearExtractionSpecs();
        handleCloseExtractionModal();
    };

    if (!awsUrl) {
        return <div>Invalid PDF URL</div>;
    }

    const extract = (metadata: MetadataType) => {
        const validation = validateImageMetadata(metadata);
        if (validation.hasError) {
            const errorMessage = Object.values(validation.errors).join('\n');
            Alert.warn(errorMessage);
            return;
        }

        setExtractionInProgress(true);
        const resourceProps = new ResourceProps(
            metadata.finDocType,
            metadata.reportType,
            metadata.note,
            metadata.suffix,
            'png',
            null,
            metadata.originalOnly,
            metadata.sourceType
        );

        if (!ResourceProps.valid(resourceProps)) {
            Alert.warn('The Financial Document Type, Report Type,and Source Type must be configured.');
            return;
        }

        const data: ExtractionDataType = {
            srcPdfUrl: awsUrl,
            imageFileName: `${ResourceProps.serialize(resourceProps)}`,
            originalOnly: resourceProps.originalOnly,
            coordinates: extractionSpecs.map((mergeCandidate) => ({
                pageNumber: mergeCandidate.page - 1,
                x: mergeCandidate.xPercentage,
                y: mergeCandidate.yPercentage,
                width: mergeCandidate.widthPercentage,
                height: mergeCandidate.heightPercentage,
            })),
        };

        ResourceRepo.extractTableFromPDF(
            data,
            (response: ExtractionResponseType) => {
                Alert.success(`Extraction succeeded for ${ResourceProps.userFriendlyName(resourceProps)}`);
                clearExtractionSpecs();
                addCompletedExtraction({
                    userFriendlyFileName: ResourceProps.userFriendlyName(resourceProps),
                    url: `${response.serverUrl}/${response.path}`,
                });
                setExtractionInProgress(false);
            },
            (error: any) => {
                Alert.error(
                    `Extraction failed for${ResourceProps.userFriendlyName(resourceProps)}. Error:${JSON.stringify(
                        error
                    )}`
                );
                clearExtractionSpecs();
                setExtractionInProgress(false);
            }
        );

        handleCloseExtractionModal();
    };

    const handleExtraction = (extractionSpec: ExtractionSpecType) => {
        addExtractionSpec(extractionSpec);
        setIsModalOpen(true);
    };

    const handleRemoveExtractionSpec = (id: string) => {
        const newExtractionSpecs = extractionSpecs.filter((x) => x.id !== id);
        if (newExtractionSpecs.length) {
            setExtractionSpecs(newExtractionSpecs);
        } else {
            handleDeleteExtractionSpecs();
        }
    };

    return (
        <div className="extraction">
            <ExtractionHeader
                completedExtractions={completedExtractions}
                pageMode={pageMode}
                setPageMode={handlePageModeChange}
                currentPage={currentPage}
                handleNextPage={handleNextPage}
                handlePrevPage={handlePrevPage}
                handleSetPage={handleSetPage}
                numberOfPages={numberOfPages}
                screenshotsEnabled={screenshotsEnabled}
                toggleScreenshotsEnabled={toggleScreenshotsEnabled}
                scale={scale}
                onScaleChange={setScale}
                thumbnailPanelVisible={thumbnailPanelVisible}
                toggleThumbnailPanel={toggleThumbnailPanel}
            />
            <div className="document-container">
                <SimpleBar autoHide={false}>
                    <Document loading={<Loader />} file={awsUrl} onLoadSuccess={onDocumentLoadSuccess}>
                        {thumbnailPanelVisible && (
                            <ThumbnailPanel
                                items={thumbnailItems}
                                currentPage={currentPage}
                                onPageSelect={handleSetPage}
                            />
                        )}
                        <div className="document-container__page-area">
                            {pageMode === PAGE_MODE.MULTI_PAGE ? (
                                <MultiPageView
                                    currentPage={currentPage}
                                    selectionDisabled={selectionDisabled}
                                    handleExtraction={handleExtraction}
                                    numberOfPages={numberOfPages}
                                    extractionSpecs={extractionSpecs}
                                    scale={scale}
                                />
                            ) : (
                                <SinglePageView
                                    extractionSpecs={extractionSpecs}
                                    currentPage={currentPage}
                                    selectionDisabled={selectionDisabled}
                                    handleExtraction={handleExtraction}
                                    numberOfPages={numberOfPages}
                                    scale={scale}
                                />
                            )}
                        </div>
                    </Document>
                </SimpleBar>
            </div>

            <ExtractionModal
                awsUrl={awsUrl}
                isLoading={extractionInProgress}
                open={isModalOpen}
                onDelete={handleDeleteExtractionSpecs}
                onExtraction={extract}
                extractionSpecs={extractionSpecs}
                onClose={handleCloseExtractionModal}
                removeExtractionSpec={handleRemoveExtractionSpec}
            />
            {!!extractionSpecs.length && (
                <ExtractionIndication
                    loading={extractionInProgress}
                    disabled={extractionInProgress}
                    handleOpenExtractionModal={handleOpenExtractionModal}
                />
            )}
        </div>
    );
};

export default PDFViewer;
